import type { OrbitLayer, Vec3, WorldLook } from "./types";

const BASES: WorldLook[] = [
  { colorA: "#151718", colorB: "#8a5d36", atmosphere: "#d8a36a", seed: 1.7, radius: 0.95, ring: true, habitable: false },
  { colorA: "#061b1c", colorB: "#427d68", atmosphere: "#70e6d4", seed: 4.2, radius: 0.95, ring: false, habitable: true },
  { colorA: "#1a111d", colorB: "#6b3d68", atmosphere: "#d48cff", seed: 8.4, radius: 0.95, ring: true, habitable: false },
  { colorA: "#07191b", colorB: "#2f7278", atmosphere: "#7df9ff", seed: 12.1, radius: 0.95, ring: false, habitable: true },
  { colorA: "#120e16", colorB: "#4c465f", atmosphere: "#8d91ff", seed: 15.8, radius: 0.95, ring: false, habitable: false },
];

export const DEFAULT_LOOK: WorldLook = { ...BASES[1] };

function hexToRgb(hex: string) {
  const n = parseInt(hex.replace("#", "").slice(0, 6), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number) {
  const h = (v: number) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s, l };
}

function hslToRgb(h: number, s: number, l: number) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = ((h % 360) + 360) % 360 / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

function shiftHex(hex: string, dh: number, ds = 0, dl = 0) {
  const { r, g, b } = hexToRgb(hex);
  const hsl = rgbToHsl(r, g, b);
  const rgb = hslToRgb(hsl.h + dh, Math.min(1, Math.max(0, hsl.s + ds)), Math.min(1, Math.max(0, hsl.l + dl)));
  return rgbToHex(rgb.r, rgb.g, rgb.b);
}

function mixHex(a: string, b: string, t: number) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex(A.r + (B.r - A.r) * t, A.g + (B.g - A.g) * t, A.b + (B.b - A.b) * t);
}

export function hashStr(s: string) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function lookFromQuery(topic: string): WorldLook {
  const h = hashStr(topic);
  const base = BASES[h % BASES.length];
  const drift = (h % 29) - 14;
  return {
    ...base,
    colorA: shiftHex(base.colorA, drift * 0.4, 0, -0.02),
    colorB: shiftHex(base.colorB, drift, 0, 0),
    atmosphere: shiftHex(base.atmosphere, drift, 0.02, 0),
    seed: base.seed + (h % 800) / 90,
    radius: 0.95,
  };
}

export function expandLook(look: WorldLook): WorldLook {
  return { ...look, radius: 0.95 };
}

export function childLook(parent: WorldLook, accent: string, layer: OrbitLayer, index: number): WorldLook {
  const drift = ((index * 19 + layer.length * 11) % 36) - 18;
  const mixT = accent ? 0.3 : 0.1;
  const atmosphere = mixHex(shiftHex(parent.atmosphere, drift, 0, 0.02), accent || parent.atmosphere, mixT);
  const colorB = mixHex(shiftHex(parent.colorB, drift * 0.7), atmosphere, 0.28);
  const colorA = mixHex(parent.colorA, shiftHex(parent.colorA, drift * 0.35), 0.45);
  const bits = (index * 13 + Math.round(parent.seed * 11) + layer.length) % 8;
  const sizeJitter = ((index * 7) % 5) * 0.018;
  return {
    colorA,
    colorB,
    atmosphere,
    seed: parent.seed + index * 1.17 + (layer === "outer" ? 4.1 : layer === "middle" ? 1.8 : 0.3),
    radius:
      layer === "inner" ? 0.5 + sizeJitter : layer === "middle" ? 0.34 + sizeJitter : 0.22 + sizeJitter,
    ring: parent.ring ? bits % 3 !== 1 : bits % 3 === 0,
    habitable: parent.habitable ? bits % 2 === 0 : bits % 5 === 0,
  };
}

export function randSphere(): Vec3 {
  const u = Math.random() * 2 - 1;
  const t = Math.random() * Math.PI * 2;
  const s = Math.sqrt(Math.max(0, 1 - u * u));
  return { x: s * Math.cos(t), y: s * Math.sin(t), z: u };
}

export function normalize(v: Vec3): Vec3 {
  const m = Math.hypot(v.x, v.y, v.z) || 1;
  return { x: v.x / m, y: v.y / m, z: v.z / m };
}

export function rotateAround(v: Vec3, axis: Vec3, angle: number): Vec3 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const dot = axis.x * v.x + axis.y * v.y + axis.z * v.z;
  return {
    x: v.x * c + (axis.y * v.z - axis.z * v.y) * s + axis.x * dot * (1 - c),
    y: v.y * c + (axis.z * v.x - axis.x * v.z) * s + axis.y * dot * (1 - c),
    z: v.z * c + (axis.x * v.y - axis.y * v.x) * s + axis.z * dot * (1 - c),
  };
}

export function branchDirection(index: number, count: number): Vec3 {
  const evenT = (index / Math.max(1, count)) * Math.PI * 2;
  const even: Vec3 = {
    x: Math.cos(evenT),
    y: Math.sin(evenT * 1.13),
    z: Math.sin(index * 1.67) * 0.72,
  };
  const rnd = randSphere();
  return normalize({
    x: even.x * 0.38 + rnd.x * 0.62,
    y: even.y * 0.38 + rnd.y * 0.62,
    z: even.z * 0.38 + rnd.z * 0.62,
  });
}
